import { STRIPE_MANAGED_REVENUE_ENTITLEMENT_KEYS } from "@hellocashflow/domain";
import { skipToken } from "@reduxjs/toolkit/query";
import { useRef, useState } from "react";

import { useAdminPermissions } from "@/hooks/useAdminPermissions";
import { parseJsonInput } from "@/pages/AdminV2/Revenue/revenueJson";
import {
  buildEntitlementUpsertPayload,
  type EntitlementsUpsertDraft,
  type EntitlementUpsertPayload,
  hasScopedEntitlementsFilter,
  toEntitlementSource,
} from "@/pages/AdminV2/Settings/entitlementsPageHelpers";
import { buildSettingsScopeQueryArgs } from "@/pages/AdminV2/Settings/settingsScopeUtils";
import {
  useGetAdminV2EntitlementsQuery,
  useGetAdminV2MeQuery,
  useUpsertAdminV2EntitlementMutation,
} from "@/store/api/baseApi";
import {
  VNextButton,
  VNextCard,
  VNextDialog,
  VNextField,
  VNextInput,
  VNextKeyValueList,
  VNextNotice,
  VNextSelect,
  VNextTextArea,
} from "@/vnext/primitives";
import { ProductWorkspaceChrome } from "@/vnext/routes/product/ProductWorkspaceChrome";
import {
  extractPublicMutationError,
  type PublicMutationErrorDetails,
} from "@/vnext/routes/settings/extractPublicMutationError";

const ENTITLEMENT_KEY_HINT = `Required. Stripe-managed keys (${STRIPE_MANAGED_REVENUE_ENTITLEMENT_KEYS.join(
  ", ",
)}) need an Organisation ID and an Expires at date.`;

const INITIAL_UPSERT_DRAFT: EntitlementsUpsertDraft = {
  entitlementKey: "",
  expiresAt: "",
  isEnabled: true,
  organisationId: "",
  source: "admin",
  startsAt: "",
  valueRaw: "{}",
};

const UUID_LIKE_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface PendingEntitlementSave {
  payload: EntitlementUpsertPayload;
  effectCopy: string;
  items: Array<{
    label: string;
    value: string;
  }>;
}

const parseOptionalIsoTimestamp = (value: string): Date | null => {
  const trimmedValue = value.trim();
  if (trimmedValue.length === 0) {
    return null;
  }

  const parsedValue = new Date(trimmedValue);
  return Number.isFinite(parsedValue.getTime()) ? parsedValue : null;
};

const formatOptionalReviewValue = (value: string | undefined): string =>
  value && value.trim().length > 0 ? value : "Not set";

const buildPendingEntitlementSave = (
  payload: EntitlementUpsertPayload,
): PendingEntitlementSave => {
  const scope = payload.organisationId
    ? `Organisation ${payload.organisationId}`
    : "Global entitlement";
  const targetState = payload.isEnabled ? "Enabled" : "Disabled";

  return {
    payload,
    effectCopy: `${payload.entitlementKey} will be saved as ${targetState.toLowerCase()} for ${scope.toLowerCase()}.`,
    items: [
      {
        label: "Entitlement",
        value: payload.entitlementKey,
      },
      {
        label: "Scope",
        value: scope,
      },
      {
        label: "Target state",
        value: targetState,
      },
      {
        label: "Source",
        value: payload.source === "admin" ? "Admin" : "System",
      },
      {
        label: "Starts",
        value: formatOptionalReviewValue(payload.startsAt),
      },
      {
        label: "Expires",
        value: formatOptionalReviewValue(payload.expiresAt),
      },
      {
        label: "Guardrail",
        value:
          "Only this entitlement record is saved, with the existing audit trail.",
      },
    ],
  };
};

export default function EntitlementsRoute() {
  const { data: meData } = useGetAdminV2MeQuery();
  const { hasPermission } = useAdminPermissions(meData?.permissionKeys);
  const canManageEntitlements = hasPermission("entitlements.manage");
  const [organisationIdFilter, setOrganisationIdFilter] = useState("");
  const [includeGlobal, setIncludeGlobal] = useState(true);
  const [draft, setDraft] =
    useState<EntitlementsUpsertDraft>(INITIAL_UPSERT_DRAFT);
  const [feedback, setFeedback] = useState<{
    details?: PublicMutationErrorDetails;
    tone: "danger" | "info";
  } | null>(null);
  const [pendingEntitlementSave, setPendingEntitlementSave] =
    useState<PendingEntitlementSave | null>(null);
  const [isConfirmingEntitlementSave, setIsConfirmingEntitlementSave] =
    useState(false);
  const entitlementSaveInFlightRef = useRef(false);
  const hasScopedOrganisationFilter =
    hasScopedEntitlementsFilter(organisationIdFilter);
  const scopedOrganisationFilter = organisationIdFilter.trim();
  const hasValidScopedOrganisationFilter =
    !hasScopedOrganisationFilter ||
    UUID_LIKE_PATTERN.test(scopedOrganisationFilter);
  const draftOrganisationId = draft.organisationId.trim();
  const hasDraftOrganisationId = draftOrganisationId.length > 0;
  const hasValidDraftOrganisationId =
    !hasDraftOrganisationId || UUID_LIKE_PATTERN.test(draftOrganisationId);
  const trimmedEntitlementKey = draft.entitlementKey.trim();
  const startsAtInput = draft.startsAt.trim();
  const expiresAtInput = draft.expiresAt.trim();
  const startsAtDate = parseOptionalIsoTimestamp(startsAtInput);
  const expiresAtDate = parseOptionalIsoTimestamp(expiresAtInput);
  const hasValidStartsAt = startsAtInput.length === 0 || startsAtDate !== null;
  const hasValidExpiresAt =
    expiresAtInput.length === 0 || expiresAtDate !== null;
  const hasValidEntitlementWindow =
    startsAtDate === null ||
    expiresAtDate === null ||
    expiresAtDate.getTime() > startsAtDate.getTime();
  const query = useGetAdminV2EntitlementsQuery(
    canManageEntitlements
      ? buildSettingsScopeQueryArgs({
          includeGlobal,
          limit: 300,
          organisationIdFilter: hasValidScopedOrganisationFilter
            ? scopedOrganisationFilter
            : "",
        })
      : skipToken,
  );
  const [upsertEntitlement, upsertState] =
    useUpsertAdminV2EntitlementMutation();
  const canSaveEntitlement =
    canManageEntitlements &&
    !upsertState.isLoading &&
    pendingEntitlementSave === null &&
    trimmedEntitlementKey.length > 0 &&
    hasValidDraftOrganisationId &&
    hasValidStartsAt &&
    hasValidExpiresAt &&
    hasValidEntitlementWindow;

  const requestSaveEntitlement = () => {
    setFeedback(null);
    if (trimmedEntitlementKey.length === 0) {
      setFeedback({
        details: {
          message: "Entitlement key is required before saving.",
        },
        tone: "danger",
      });
      return;
    }

    if (!hasValidDraftOrganisationId) {
      setFeedback({
        details: {
          message: "Organisation ID must be a valid UUID when provided.",
        },
        tone: "danger",
      });
      return;
    }

    if (!hasValidStartsAt) {
      setFeedback({
        details: {
          message: "Starts at must be a valid ISO timestamp when provided.",
        },
        tone: "danger",
      });
      return;
    }

    if (!hasValidExpiresAt) {
      setFeedback({
        details: {
          message: "Expires at must be a valid ISO timestamp when provided.",
        },
        tone: "danger",
      });
      return;
    }

    if (!hasValidEntitlementWindow) {
      setFeedback({
        details: {
          message: "Expires at must be later than Starts at.",
        },
        tone: "danger",
      });
      return;
    }

    const valueResult = parseJsonInput({
      allowEmpty: true,
      label: "Entitlement value JSON",
      raw: draft.valueRaw,
    });
    if (valueResult.error) {
      setFeedback({
        details: { message: valueResult.error },
        tone: "danger",
      });
      return;
    }

    setPendingEntitlementSave(
      buildPendingEntitlementSave(
        buildEntitlementUpsertPayload({
          draft,
          parsedValue: valueResult.value,
        }),
      ),
    );
  };

  const closeEntitlementSaveConfirmation = () => {
    if (isConfirmingEntitlementSave) {
      return;
    }
    setPendingEntitlementSave(null);
  };

  const confirmEntitlementSave = async () => {
    if (!pendingEntitlementSave || entitlementSaveInFlightRef.current) {
      return;
    }

    entitlementSaveInFlightRef.current = true;
    setIsConfirmingEntitlementSave(true);
    setFeedback(null);
    try {
      await upsertEntitlement(pendingEntitlementSave.payload).unwrap();
      setFeedback({
        details: { message: "Entitlement saved." },
        tone: "info",
      });
      setPendingEntitlementSave(null);
      await query.refetch();
    } catch (error) {
      setFeedback({
        details: extractPublicMutationError(
          error,
          "Entitlement could not be saved.",
        ),
        tone: "danger",
      });
      setPendingEntitlementSave(null);
    } finally {
      entitlementSaveInFlightRef.current = false;
      setIsConfirmingEntitlementSave(false);
    }
  };

  const entitlements = query.data?.entitlements ?? [];

  return (
    <ProductWorkspaceChrome
      subtitle="Manage access entitlements used by revenue evaluation."
      title="Entitlements"
    >
      <div className="vnext-stack">
        {pendingEntitlementSave ? (
          <VNextDialog
            description="Review before saving this entitlement."
            footer={
              <>
                <VNextButton
                  disabled={isConfirmingEntitlementSave}
                  onClick={closeEntitlementSaveConfirmation}
                  tone="secondary"
                >
                  Cancel
                </VNextButton>
                <VNextButton
                  disabled={
                    isConfirmingEntitlementSave || upsertState.isLoading
                  }
                  onClick={() => {
                    void confirmEntitlementSave();
                  }}
                  tone="primary"
                >
                  {isConfirmingEntitlementSave
                    ? "Saving..."
                    : "Save entitlement"}
                </VNextButton>
              </>
            }
            onClose={closeEntitlementSaveConfirmation}
            open
            title="Save entitlement"
          >
            <div className="vnext-stack">
              <p>{pendingEntitlementSave.effectCopy}</p>
              <VNextKeyValueList items={pendingEntitlementSave.items} />
            </div>
          </VNextDialog>
        ) : null}
        {feedback ? (
          <VNextNotice tone={feedback.tone}>
            <div>{feedback.details?.message}</div>
            {feedback.details?.errorCode ? (
              <div>
                Code: <code>{feedback.details.errorCode}</code>
              </div>
            ) : null}
            {feedback.details?.correlationId ? (
              <div>
                Ref: <code>{feedback.details.correlationId}</code>
              </div>
            ) : null}
          </VNextNotice>
        ) : null}
        <div className="vnext-two-pane">
          <VNextCard
            actions={
              <VNextButton
                disabled={!hasScopedOrganisationFilter}
                onClick={() => {
                  setOrganisationIdFilter("");
                  setIncludeGlobal(true);
                }}
                tone="ghost"
              >
                Clear scope
              </VNextButton>
            }
            title="Scope"
          >
            <div className="vnext-grid">
              <VNextField
                hint="Example: 123e4567-e89b-12d3-a456-426614174000"
                label="Organisation scope (UUID optional)"
              >
                <VNextInput
                  onChange={(event) =>
                    setOrganisationIdFilter(event.target.value)
                  }
                  placeholder="e.g. 123e4567-e89b-12d3-a456-426614174000"
                  value={organisationIdFilter}
                />
              </VNextField>
              <label className="vnext-field">
                <span className="vnext-field__label">Scope options</span>
                <label className="vnext-inline-actions">
                  <input
                    aria-label="Include global entitlements"
                    checked={includeGlobal}
                    disabled={
                      !hasScopedOrganisationFilter ||
                      !hasValidScopedOrganisationFilter
                    }
                    onChange={(event) => setIncludeGlobal(event.target.checked)}
                    type="checkbox"
                  />
                  <span>Include global entitlements</span>
                </label>
              </label>
              {!hasScopedOrganisationFilter ? (
                <div className="vnext-inline-warning">
                  No organisation scope selected. The list is global-only by
                  default.
                </div>
              ) : !hasValidScopedOrganisationFilter ? (
                <div className="vnext-inline-warning">
                  Enter a valid organisation UUID to scope the list. Until then,
                  only global entitlements are shown.
                </div>
              ) : null}
            </div>
          </VNextCard>

          <VNextCard title="Save entitlement">
            <div className="vnext-grid">
              <VNextField
                error={
                  hasDraftOrganisationId && !hasValidDraftOrganisationId
                    ? "Enter a valid organisation UUID."
                    : null
                }
                hint="Optional. Leave blank to save a global entitlement."
                label="Organisation ID"
              >
                <VNextInput
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      organisationId: event.target.value,
                    }))
                  }
                  placeholder="e.g. 123e4567-e89b-12d3-a456-426614174000"
                  value={draft.organisationId}
                />
              </VNextField>
              <VNextField hint={ENTITLEMENT_KEY_HINT} label="Entitlement key">
                <VNextInput
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      entitlementKey: event.target.value,
                    }))
                  }
                  placeholder="e.g. scenario_calculator_v2"
                  value={draft.entitlementKey}
                />
              </VNextField>
              <label className="vnext-field">
                <span className="vnext-field__label">Enabled</span>
                <VNextSelect
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      isEnabled: event.target.value === "true",
                    }))
                  }
                  value={String(draft.isEnabled)}
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </VNextSelect>
              </label>
              <label className="vnext-field">
                <span className="vnext-field__label">Source</span>
                <VNextSelect
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      source: toEntitlementSource(
                        event.target.value,
                        current.source,
                      ),
                    }))
                  }
                  value={draft.source}
                >
                  <option value="admin">Admin</option>
                  <option value="system">System</option>
                </VNextSelect>
              </label>
              <VNextField
                error={
                  startsAtInput.length > 0 && !hasValidStartsAt
                    ? "Enter a valid ISO timestamp."
                    : null
                }
                hint="Optional. Example: 2026-12-31T23:59:59.000Z"
                label="Starts at (UTC, ISO)"
              >
                <VNextInput
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      startsAt: event.target.value,
                    }))
                  }
                  placeholder="e.g. 2026-12-31T23:59:59.000Z"
                  value={draft.startsAt}
                />
              </VNextField>
              <VNextField
                error={
                  expiresAtInput.length > 0 && !hasValidExpiresAt
                    ? "Enter a valid ISO timestamp."
                    : !hasValidEntitlementWindow
                      ? "Expires at must be later than Starts at."
                      : null
                }
                hint="Optional. Example: 2027-01-31T23:59:59.000Z"
                label="Expires at (UTC, ISO)"
              >
                <VNextInput
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      expiresAt: event.target.value,
                    }))
                  }
                  placeholder="e.g. 2027-01-31T23:59:59.000Z"
                  value={draft.expiresAt}
                />
              </VNextField>
              <label className="vnext-field">
                <span className="vnext-field__label">Value JSON</span>
                <VNextTextArea
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      valueRaw: event.target.value,
                    }))
                  }
                  placeholder="{}"
                  rows={6}
                  value={draft.valueRaw}
                />
              </label>
              <div className="vnext-inline-actions">
                <VNextButton
                  disabled={!canSaveEntitlement}
                  onClick={requestSaveEntitlement}
                  tone="primary"
                >
                  Save entitlement
                </VNextButton>
              </div>
            </div>
          </VNextCard>
        </div>

        <VNextCard
          subtitle="The table follows the scope controls above so you can compare global and organisation-specific access."
          title="Entitlements"
        >
          {query.isLoading ? (
            <div className="vnext-empty">Loading entitlements…</div>
          ) : query.isError ? (
            <VNextNotice tone="danger">
              Entitlements could not be loaded.
            </VNextNotice>
          ) : entitlements.length === 0 ? (
            <div className="vnext-empty">
              No entitlements matched the current scope.
            </div>
          ) : (
            <div className="vnext-scroll-panel">
              <table className="vnext-table">
                <thead>
                  <tr>
                    <th>Entitlement</th>
                    <th>Scope</th>
                    <th>Enabled</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {entitlements.map((entitlement) => (
                    <tr key={entitlement.id}>
                      <td>{entitlement.entitlementKey}</td>
                      <td>{entitlement.organisationId ?? "global"}</td>
                      <td>{entitlement.isEnabled ? "Enabled" : "Disabled"}</td>
                      <td>{entitlement.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </VNextCard>
      </div>
    </ProductWorkspaceChrome>
  );
}
